import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { clamp, prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { useLenis } from '@/components/motion/smooth-scroll'

interface MarqueeProps {
    children: ReactNode
    /** Base speed in pixels per second. */
    speed?: number
    /** 1 moves left, -1 moves right. */
    direction?: 1 | -1
    /** How strongly scroll speed boosts the marquee. */
    velocity?: number
    copies?: number
    className?: string
    trackClassName?: string
}

/**
 * Endless marquee that speeds up with scroll velocity and flips direction
 * when the visitor scrolls back up.
 */
const Marquee: FC<MarqueeProps> = ({
    children,
    speed = 60,
    direction = 1,
    velocity = 0.12,
    copies = 4,
    className,
    trackClassName
}): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const track = useRef<HTMLDivElement>(null)
    const lenis = useLenis()

    useEffect(() => {
        const rootEl = root.current
        const trackEl = track.current

        if (!rootEl || !trackEl) return

        let width = 0
        let x = 0
        let boost = 0
        let scrollDirection = 1
        let visible = true

        const measure = (): void => {
            const first = trackEl.firstElementChild as HTMLElement | null

            width = first?.offsetWidth ?? 0
        }

        measure()

        const resize = new ResizeObserver(measure)

        resize.observe(trackEl)

        const observer = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting
        }, { rootMargin: '120px' })

        observer.observe(rootEl)

        const offScroll = lenis?.on('scroll', instance => {
            boost = clamp(Math.abs(instance.velocity) * velocity, 0, 14)

            if (instance.direction !== 0) scrollDirection = instance.direction
        })

        const reduced = prefersReducedMotion()

        const tick = (_time: number, deltaTime: number): void => {
            if (!visible || !width || reduced) return

            const delta = Math.min(deltaTime, 64) / 1000

            x -= speed * delta * (1 + boost) * direction * scrollDirection
            boost *= 0.92

            if (x <= -width) x += width
            if (x > 0) x -= width

            trackEl.style.transform = `translate3d(${x}px, 0, 0)`
        }

        gsap.ticker.add(tick)

        return () => {
            gsap.ticker.remove(tick)
            resize.disconnect()
            observer.disconnect()
            offScroll?.()
        }
    }, [lenis, speed, direction, velocity])

    return (
        <div ref={root} className={cn('overflow-hidden', className)}>
            <div ref={track} className={cn('marquee-track', trackClassName)}>
                {Array.from({ length: copies }, (_, index) => (
                    <div key={index} aria-hidden={index > 0 ? 'true' : undefined} className='flex shrink-0 items-center'>
                        {children}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default Marquee
